import { LitElement, css, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';

@customElement('lit-animated-snow')
export class LitAnimatedSnow extends LitElement {
    @query('canvas')
    private canvas!: HTMLCanvasElement;

    private ctx!: CanvasRenderingContext2D | null;
    private animationFrameId: number = 0;
    private flakes: Array<{ x: number; y: number; r: number; s: number }> = [];

    private handleResize = () => this.resizeCanvas();
    private resizeObserver: ResizeObserver | null = null;
    private resizeTimeout: number = 0;
    private lastWidth: number = 0;
    private lastHeight: number = 0;

    static styles = css`
        :host {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            display: block;
            pointer-events: none;
            z-index: 1;
        }

        canvas {
            width: 100%;
            height: 100%;
            display: block;
        }
    `;

    render() {
        return html`<canvas></canvas>`;
    }

    firstUpdated() {
        const canvas = this.canvas;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            this.ctx = null;
            return;
        }
        this.ctx = ctx;

        // Użyj host element jako kontener
        const observedTarget = this;

        // Debounced resize handler
        const debouncedResize = () => {
            if (this.resizeTimeout) {
                window.cancelAnimationFrame(this.resizeTimeout);
            }
            this.resizeTimeout = window.requestAnimationFrame(() => {
                this.resizeCanvas();
                this.createFlakesIfNeeded();
            });
        };

        // ResizeObserver z fallback
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(debouncedResize);
            this.resizeObserver.observe(observedTarget);
        } else {
            console.warn('ResizeObserver not supported, using window resize only');
        }

        // Window resize jako fallback
        window.addEventListener('resize', this.handleResize);

        // Inicjalizacja
        this.resizeCanvas();
        this.createFlakes();
        this.loop();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        cancelAnimationFrame(this.animationFrameId);
        window.removeEventListener('resize', this.handleResize);

        if (this.resizeTimeout) {
            window.cancelAnimationFrame(this.resizeTimeout);
        }

        this.resizeObserver?.disconnect?.();
        this.resizeObserver = null;
    }

    private resizeCanvas() {
        if (!this.canvas || !this.ctx) return;

        // Pobierz wymiary host elementu
        const hostRect = this.getBoundingClientRect();
        const hostW = hostRect.width;
        const hostH = hostRect.height;

        if (hostW <= 0 || hostH <= 0) return;

        // Sprawdź czy rozmiar rzeczywiście się zmienił
        if (Math.abs(hostW - this.lastWidth) < 1 && Math.abs(hostH - this.lastHeight) < 1) {
            return;
        }

        this.lastWidth = hostW;
        this.lastHeight = hostH;

        const dpr = Math.max(1, window.devicePixelRatio || 1);

        // Reset transformacji
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);

        // Ustaw rozmiar canvas w pikselach fizycznych
        this.canvas.width = Math.round(hostW * dpr);
        this.canvas.height = Math.round(hostH * dpr);

        // Skalowanie dla DPI
        this.ctx.scale(dpr, dpr);

        // Ustaw CSS rozmiar
        this.canvas.style.width = `${hostW}px`;
        this.canvas.style.height = `${hostH}px`;
    }

    private createFlakesIfNeeded() {
        if (!this.canvas) return;

        const W = this.canvas.clientWidth;
        const H = this.canvas.clientHeight;

        if (!W || !H || !this.flakes.length) {
            this.createFlakes();
        } else if (Math.abs(W - this.lastWidth) > 10 || Math.abs(H - this.lastHeight) > 10) {
            // Tylko przy większych zmianach rozmiaru generuj nowe płatki
            this.createFlakes();
        }
    }

    private createFlakes() {
        if (!this.canvas) return;

        const W = this.canvas.clientWidth || this.lastWidth;
        const H = this.canvas.clientHeight || this.lastHeight;

        if (!W || !H) {
            this.flakes = [];
            return;
        }

        // Liczba płatków zależna od obszaru
        const area = W * H;
        const count = Math.max(30, Math.min(200, Math.round(area / 1000)));

        this.flakes = Array.from({ length: count }, () => ({
            x: Math.random() * W,
            y: Math.random() * H,
            r: Math.random() * 2.5 + 0.5,
            s: Math.random() * 0.8 + 0.2,
        }));
    }

    private drawSnow() {
        if (!this.ctx || !this.canvas) return;

        const W = this.canvas.clientWidth || this.lastWidth;
        const H = this.canvas.clientHeight || this.lastHeight;

        if (!W || !H || this.flakes.length === 0) return;

        const ctx = this.ctx;

        // Wyczyszczenie canvas z uwzględnieniem DPI
        const dpr = Math.max(1, window.devicePixelRatio || 1);
        ctx.clearRect(0, 0, W * dpr, H * dpr);

        this.flakes.forEach((f) => {
            ctx.beginPath();
            ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fill();

            // Animacja
            f.y += f.s;
            f.x += Math.sin(f.y * 0.01) * 0.5;

            // Reset płatka gdy wyjdzie poza dół
            if (f.y - f.r > H) {
                f.y = -f.r;
                f.x = Math.random() * W;
            }

            // Reset płatka gdy wyjdzie poza boki (dla bezpieczeństwa)
            if (f.x < -f.r) f.x = W + f.r;
            if (f.x > W + f.r) f.x = -f.r;
        });
    }

    private loop = () => {
        this.drawSnow();
        this.animationFrameId = requestAnimationFrame(this.loop);
    };
}
